import { ComponentProps, createContext, useContext, MouseEvent, useState } from 'react';
import * as SingleFilterPrimitive from '@radix-ui/react-select';

import { mergeClasses } from '@/utils';

import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import { Label } from '@/components/common';

interface ISingleFilterContextValue {
  onValueChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
  error?: string;
  isOpen?: boolean;
}

const SingleFilterContext = createContext<ISingleFilterContextValue>({});

const SingleFilter = ({
  className,
  label,
  onValueChange,
  value,
  defaultValue,
  required,
  error,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Root> & {
  className?: string;
  label?: string;
  defaultValue?: string;
  error?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SingleFilterContext.Provider value={{ onValueChange, value, defaultValue, error, isOpen }}>
      <div className={mergeClasses('flex flex-col gap-1.5', className)}>
        {label && (
          <Label className="!gap-0 font-semibold">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </Label>
        )}
        <SingleFilterPrimitive.Root
          key={value ?? 'empty'}
          data-slot="select"
          value={value}
          onValueChange={onValueChange}
          open={isOpen}
          onOpenChange={setIsOpen}
          required={required}
          {...props}
        />
        {error && <p className="text-xs text-semantic-error-base font-medium">{error}</p>}
      </div>
    </SingleFilterContext.Provider>
  );
};

const SingleFilterGroup = ({ ...props }: ComponentProps<typeof SingleFilterPrimitive.Group>) => {
  return <SingleFilterPrimitive.Group data-slot="select-group" {...props} />;
};

const SingleFilterValue = ({ ...props }: ComponentProps<typeof SingleFilterPrimitive.Value>) => {
  return <SingleFilterPrimitive.Value data-slot="select-value" {...props} />;
};

const SingleFilterTrigger = ({
  className,
  size = 'default',
  allowClear = false,
  children,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Trigger> & {
  size?: 'sm' | 'default';
  allowClear?: boolean;
}) => {
  const { onValueChange, value, defaultValue, error, isOpen } = useContext(SingleFilterContext);
  const showClearButton = allowClear && Boolean(value);

  const clearSingleFilterion = (e: MouseEvent) => {
    e.stopPropagation();
    onValueChange?.(undefined as any);
  };

  return (
    <div className="relative inline-block w-full">
      <SingleFilterPrimitive.Trigger
        data-slot="select-trigger"
        data-size={size}
        className={mergeClasses(
          "shadow-none rounded-xs border-neutral-4 text-neutral-9 font-medium hover:border-neutral-6 focus:border-primary-base data-[state=open]:border-primary-base [&_svg]:text-primary-darkest aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center gap-2 border bg-neutral-0 pl-3 py-2 text-body-sm whitespace-nowrap transition-[color,box-shadow] outline-none data-[size=default]:h-10 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          showClearButton ? 'pr-14' : 'pr-8',
          value === defaultValue && '[&_[data-slot=select-value]]:text-neutral-7',
          error && '!border-semantic-error-base',
          props.disabled && '!bg-neutral-2 !border-neutral-4 text-neutral-6 cursor-not-allowed',
          className,
        )}
        {...props}
      >
        {children}
        <SingleFilterPrimitive.Icon asChild>
          {isOpen ? (
            <ChevronUpIcon className="size-4 absolute right-3 !text-primary-base disabled:text-neutral-6" />
          ) : (
            <ChevronDownIcon className="size-4 absolute right-3 disabled:text-neutral-6" />
          )}
        </SingleFilterPrimitive.Icon>
      </SingleFilterPrimitive.Trigger>
      {showClearButton && (
        <button
          type="button"
          onClick={clearSingleFilterion}
          className="text-neutral-6 hover:text-neutral-8 absolute top-1/2 right-9 size-4 -translate-y-1/2 flex items-center justify-center transition-colors z-10"
          aria-label="Clear selection"
        >
          <CloseIcon className="w-base h-base" />
        </button>
      )}
    </div>
  );
};

const SingleFilterContent = ({
  className,
  children,
  position = 'popper',
  align = 'center',
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Content>) => {
  return (
    <SingleFilterPrimitive.Portal>
      <SingleFilterPrimitive.Content
        data-slot="select-content"
        className={mergeClasses(
          'max-h-[var(--radix-select-content-available-height)] bg-neutral-0 text-neutral-9 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[9999] max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        align={align}
        {...props}
      >
        <SingleFilterPrimitive.Viewport
          className={mergeClasses(
            'py-s !overflow-visible',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SingleFilterPrimitive.Viewport>
      </SingleFilterPrimitive.Content>
    </SingleFilterPrimitive.Portal>
  );
};

const SingleFilterLabel = ({
  className,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Label>) => {
  return (
    <SingleFilterPrimitive.Label
      data-slot="select-label"
      className={mergeClasses('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
};

const SingleFilterItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Item>) => {
  return (
    <SingleFilterPrimitive.Item
      data-slot="select-item"
      className={mergeClasses(
        "cursor-pointer outline-none focus:bg-neutral-2 text-body-sm size-xl pl-base text-neutral-9 font-medium focus:text-neutral-9 [&_svg:not([class*='text-'])]:text-neutral-9 relative flex w-full items-center gap-2 rounded-sm py-1.5 pr-8 select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 data-[state=checked]:text-primary-base h-10",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SingleFilterPrimitive.ItemIndicator>
          <CheckIcon className="size-5 text-primary-base" />
        </SingleFilterPrimitive.ItemIndicator>
      </span>
      <SingleFilterPrimitive.ItemText>{children}</SingleFilterPrimitive.ItemText>
    </SingleFilterPrimitive.Item>
  );
};

const SingleFilterSeparator = ({
  className,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.Separator>) => {
  return (
    <SingleFilterPrimitive.Separator
      data-slot="select-separator"
      className={mergeClasses('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
};

const SingleFilterScrollUpButton = ({
  className,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.ScrollUpButton>) => {
  return (
    <SingleFilterPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={mergeClasses('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SingleFilterPrimitive.ScrollUpButton>
  );
};

const SingleFilterScrollDownButton = ({
  className,
  ...props
}: ComponentProps<typeof SingleFilterPrimitive.ScrollDownButton>) => {
  return (
    <SingleFilterPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={mergeClasses('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SingleFilterPrimitive.ScrollDownButton>
  );
};

export {
  SingleFilter,
  SingleFilterContent,
  SingleFilterGroup,
  SingleFilterItem,
  SingleFilterLabel,
  SingleFilterScrollDownButton,
  SingleFilterScrollUpButton,
  SingleFilterSeparator,
  SingleFilterTrigger,
  SingleFilterValue,
};
